(() => {
  if (window.owHeroVideoInstalled) return;
  window.owHeroVideoInstalled = true;

  const initialise = (root = document) => {
    root.querySelectorAll('[data-ow-hero-video]').forEach(media => {
      if (media.dataset.owInitialised) return;
      let video = media.querySelector('video');
      if (!video) return;
      media.dataset.owInitialised = 'true';
      const controller = new AbortController();
      const {signal} = controller;
      const button = media.parentElement.querySelector('[data-ow-video-resume]');
      let videoController;
      let active = false;
      let pageActive = true;
      let leaving = false;
      let blocked = false;
      let timer;
      let restartTimer;
      let frameRequest = null;
      let lastProgress = performance.now();
      let lastTime = video.currentTime;
      let lastBufferGrowth = performance.now();
      let bufferedEnd = 0;
      let recovery = 0;
      let healthySince = null;
      let generation = 0;
      let exitFrame;
      let exitTimer;

      const eligible = () => {
        if (!media.isConnected || !pageActive || leaving || document.hidden) return false;
        const rect = media.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      };
      const offerPlay = value => { if (button) button.hidden = !value; };
      const releaseFrame = () => {
        exitFrame?.remove();
        exitFrame = null;
        clearTimeout(exitTimer);
      };
      const holdFrame = () => {
        if (exitFrame || video.readyState < 2 || !video.videoWidth) return;
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext('2d');
          if (!context) return;
          context.drawImage(video, 0, 0);
          canvas.setAttribute('aria-hidden', 'true');
          canvas.setAttribute('data-ow-exit-frame', '');
          Object.assign(canvas.style, {
            position: 'absolute', inset: '0', width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: getComputedStyle(video).objectPosition,
            pointerEvents: 'none', zIndex: '1'
          });
          media.append(canvas);
          exitFrame = canvas;
        } catch (_) {}
      };
      const cancelWork = () => {
        clearTimeout(timer);
        clearTimeout(restartTimer);
        restartTimer = null;
        if (frameRequest !== null) video.cancelVideoFrameCallback(frameRequest);
        frameRequest = null;
        generation++;
      };
      const stop = () => {
        active = false;
        cancelWork();
        video.pause();
      };
      const progress = time => {
        if (!active || !eligible() || video.paused || video.seeking || Math.abs(time - lastTime) < 0.01) return;
        lastTime = time;
        lastProgress = performance.now();
        healthySince ??= lastProgress;
        blocked = false;
        offerPlay(false);
        media.setAttribute('data-ow-playing', '');
        if (lastProgress - healthySince >= 5000) recovery = 0;
      };
      const watchFrame = () => {
        if (!active || frameRequest !== null || !video.requestVideoFrameCallback) return;
        frameRequest = video.requestVideoFrameCallback((_, metadata) => {
          frameRequest = null;
          progress(metadata.mediaTime);
          watchFrame();
        });
      };
      const play = () => {
        if (!active || !eligible()) return;
        const attempt = generation;
        video.defaultMuted = true;
        video.muted = true;
        video.playsInline = true;
        try {
          Promise.resolve(video.play()).catch(error => {
            if (attempt !== generation || !eligible()) return;
            if (error.name === 'NotAllowedError') {
              blocked = true;
              offerPlay(true);
            }
          });
        } catch (_) { offerPlay(true); }
        watchFrame();
      };
      const bindVideo = () => {
        videoController?.abort();
        videoController = new AbortController();
        const options = {signal: videoController.signal};
        video.addEventListener('timeupdate', () => {
          if (!video.requestVideoFrameCallback) progress(video.currentTime);
        }, options);
        video.addEventListener('progress', () => {
          const end = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
          if (end > bufferedEnd + 0.1) {
            bufferedEnd = end;
            lastBufferGrowth = performance.now();
          }
        }, options);
      };
      const replaceVideo = () => {
        const previous = video;
        const fresh = previous.cloneNode(true);
        fresh.defaultMuted = true;
        fresh.muted = true;
        fresh.playsInline = true;
        previous.replaceWith(fresh);
        video = fresh;
        bindVideo();
        previous.removeAttribute('src');
        previous.querySelectorAll('source').forEach(source => source.remove());
        previous.load();
        bufferedEnd = 0;
        lastBufferGrowth = performance.now();
      };
      const check = () => {
        if (!eligible()) { stop(); return; }
        if (!video.requestVideoFrameCallback) progress(video.currentTime);
        const now = performance.now();
        if (!blocked && now - lastProgress >= 2500 &&
            (video.readyState >= 3 || now - lastBufferGrowth >= 5000)) {
          if (recovery < 2) {
            recovery++;
            restart(recovery === 2);
            return;
          }
          offerPlay(true);
        }
        timer = setTimeout(check, 1000);
      };
      const restart = (replace = false, immediate = false) => {
        cancelWork();
        video.pause();
        if (!eligible()) { active = false; return; }
        active = true;
        blocked = false;
        healthySince = null;
        if (replace) replaceVideo();
        const begin = () => {
          restartTimer = null;
          if (!eligible()) { stop(); return; }
          lastTime = video.currentTime;
          lastProgress = performance.now();
          play();
          timer = setTimeout(check, 1000);
        };
        if (immediate) begin();
        else restartTimer = setTimeout(begin, 150);
      };
      const sync = () => {
        if (!eligible()) { stop(); return; }
        if (!active) {
          recovery = 0;
          restart();
        }
      };
      const gesture = event => {
        if (!event.isTrusted || event.target?.closest?.('a[href], button, input, select, textarea') || !eligible()) return;
        if (blocked || video.paused || performance.now() - lastProgress >= 2500) {
          recovery = 0;
          restart(false, true);
        }
      };
      button?.addEventListener('click', () => {
        if (!eligible()) return;
        const replace = recovery >= 2;
        recovery = 0;
        restart(replace, true);
      }, {signal});
      document.addEventListener('click', event => {
        if (!event.isTrusted || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        const link = event.target?.closest?.('a[href]');
        if (!link || link.hasAttribute('download')) return;
        const target = link.target || document.querySelector('base[target]')?.target || '_self';
        if (!['_self', '_parent', '_top'].includes(target)) return;
        const destination = new URL(link.href, window.location.href);
        if (destination.origin !== window.location.origin) return;
        if (destination.pathname === window.location.pathname && destination.search === window.location.search &&
            (destination.hash || link.getAttribute('href').startsWith('#'))) return;
        queueMicrotask(() => {
          if (event.defaultPrevented || !eligible()) return;
          holdFrame();
          leaving = true;
          stop();
          exitTimer = setTimeout(() => {
            if (!pageActive || document.hidden) return;
            leaving = false;
            releaseFrame();
            sync();
          }, 10000);
        });
      }, {signal});
      window.addEventListener('pagehide', () => {
        holdFrame();
        pageActive = false;
        clearTimeout(exitTimer);
        stop();
      }, {signal});
      window.addEventListener('pageshow', () => {
        pageActive = true;
        leaving = false;
        releaseFrame();
        sync();
      }, {signal});
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          leaving = false;
          releaseFrame();
        }
        sync();
      }, {signal});
      window.addEventListener('load', sync, {signal});
      window.addEventListener('resize', sync, {signal, passive: true});
      document.addEventListener('touchend', gesture, {signal, passive: true});
      document.addEventListener('pointerup', gesture, {signal, passive: true});
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(sync) : null;
      observer?.observe(media);
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(media)) return;
        pageActive = false;
        stop();
        releaseFrame();
        controller.abort();
        videoController.abort();
        observer?.disconnect();
        delete media.dataset.owInitialised;
      }, {signal});
      bindVideo();
      sync();
    });
  };
  initialise();
  document.addEventListener('shopify:section:load', event => initialise(event.target));
})();
